import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DataServerService } from '../api/data-server.service';
import { DateTime } from 'luxon';
import { StorageService } from '../data/storage.service';

interface LexwareTokenPayload {
  exp?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  constructor(
    private dataServer: DataServerService,
    private storage: StorageService
  ) {}

  TOKEN_EXPIRY_BUFFER = 300;
  public token: any = '';
  public tokenvalid: boolean = false;
  public tokenLifetime: number = 0;

  // Standard-Werte für Orders
  public defaultDeliveryTime: string = '';
  public defaultCompanyInfo: string = '';
  public defaultAgentNote: string = '';
  public karmaPhone: string = '';
  public karmaEmail: string = '';
  public karmaDeliveryDate: string = '';
  public karmaPaymentId: string = '59';
  public karmaNote: string = '';
  public karmaDefaults$ = new BehaviorSubject<{ phone: string; email: string; deliveryDate: string; paymentId: string; note: string }>({ phone: '', email: '', deliveryDate: '', paymentId: '59', note: '' });
  private defaultsId: string = ''; // Payload CMS ID für Updates

  private defaultsLoadPromise: Promise<void> | null = null;
  private defaultsLoadedFromStorage: boolean = false;

  async initSession() {
    const tok = await this.storage.getData('sessionToken');
    if (tok) {
      this.token = tok;
      await this.checkToken();
    }

    // Lade Standard-Werte von Payload CMS
    await this.loadDefaultsFromPayload();
  }

  // Standard-Werte von Payload CMS laden
  async loadDefaultsFromPayload() {
    try {
      const response = await this.dataServer.getDefaults();

      if (response.status === 200 && response.data?.docs?.length > 0) {
        const defaults = response.data.docs[0];
        this.defaultsId = defaults.id;

        // Werte aus Payload CMS übernehmen
        this.defaultDeliveryTime = defaults.deliveryNote || '';
        this.defaultCompanyInfo = defaults.companyName || '';
        this.defaultAgentNote = defaults.agentNote || '';
        this.karmaPhone = defaults.karmaPhone || '';
        this.karmaEmail = defaults.karmaEmail || '';
        this.karmaDeliveryDate = defaults.karmaDeliveryDate || '';
        this.karmaPaymentId = defaults.karmaPaymentId || '59';
        this.karmaNote = defaults.karmaNote || '';
        this.karmaDefaults$.next({ phone: this.karmaPhone, email: this.karmaEmail, deliveryDate: this.karmaDeliveryDate, paymentId: this.karmaPaymentId, note: this.karmaNote });

        // Auch lokal speichern als Fallback
        await this.storage.setData('defaultDeliveryTime', this.defaultDeliveryTime);
        await this.storage.setData('defaultCompanyInfo', this.defaultCompanyInfo);
        await this.storage.setData('defaultAgentNote', this.defaultAgentNote);
        await this.storage.setData('karmaPhone', this.karmaPhone);
        await this.storage.setData('karmaEmail', this.karmaEmail);
        await this.storage.setData('karmaDeliveryDate', this.karmaDeliveryDate);
        await this.storage.setData('karmaPaymentId', this.karmaPaymentId);
        await this.storage.setData('karmaNote', this.karmaNote);
        await this.storage.setData('defaultsId', this.defaultsId);

      } else {
        // Fallback: Lade aus lokalem Storage
        await this.loadDefaultsFromStorage();
      }
    } catch (error) {
      console.error('Fehler beim Laden der Defaults von Payload CMS:', error);
      // Fallback: Lade aus lokalem Storage
      await this.loadDefaultsFromStorage();
    }
  }

  // Fallback: Standard-Werte aus lokalem Storage laden
  async loadDefaultsFromStorage() {
    const deliveryTime = await this.storage.getData('defaultDeliveryTime');
    const companyInfo = await this.storage.getData('defaultCompanyInfo');
    const agentNote = await this.storage.getData('defaultAgentNote');
    const id = await this.storage.getData('defaultsId');

    if (deliveryTime) this.defaultDeliveryTime = deliveryTime;
    if (companyInfo) this.defaultCompanyInfo = companyInfo;
    if (agentNote) this.defaultAgentNote = agentNote;
    if (id) this.defaultsId = id;
    const kPhone = await this.storage.getData('karmaPhone');
    const kEmail = await this.storage.getData('karmaEmail');
    const kDate = await this.storage.getData('karmaDeliveryDate');
    if (kPhone) this.karmaPhone = kPhone;
    if (kEmail) this.karmaEmail = kEmail;
    if (kDate) this.karmaDeliveryDate = kDate;
    const kPayment = await this.storage.getData('karmaPaymentId');
    if (kPayment) this.karmaPaymentId = kPayment;
    const kNote = await this.storage.getData('karmaNote');
    if (kNote) this.karmaNote = kNote;
    this.karmaDefaults$.next({ phone: this.karmaPhone, email: this.karmaEmail, deliveryDate: this.karmaDeliveryDate, paymentId: this.karmaPaymentId, note: this.karmaNote });
  }

  // Für Kompatibilität mit bestehendem Code
  async loadDefaults() {
    if (this.defaultsLoadPromise) {
      return this.defaultsLoadPromise;
    }

    this.defaultsLoadPromise = (async () => {
      // Fast path: load cached values first
      if (!this.defaultsLoadedFromStorage) {
        await this.loadDefaultsFromStorage();
        this.defaultsLoadedFromStorage = true;
      }

      // Refresh from server in background. Do not block UI if it is slow.
      this.loadDefaultsFromPayload().catch((error) => {
        console.error('Fehler beim Laden der Defaults von Payload CMS:', error);
      });
    })();

    return this.defaultsLoadPromise;
  }

  // Standard-Lieferzeit setzen (lokal und in Payload CMS)
  async setDefaultDeliveryTime(value: string) {
    this.defaultDeliveryTime = value;
    await this.storage.setData('defaultDeliveryTime', value);
    await this.updatePayloadDefaults();
  }

  // Standard-Firmeninfo setzen (lokal und in Payload CMS)
  async setDefaultCompanyInfo(value: string) {
    this.defaultCompanyInfo = value;
    await this.storage.setData('defaultCompanyInfo', value);
    await this.updatePayloadDefaults();
  }

  // Standard-Agenturnotiz setzen (lokal und in Payload CMS)
  async setDefaultAgentNote(value: string) {
    this.defaultAgentNote = value;
    await this.storage.setData('defaultAgentNote', value);
    await this.updatePayloadDefaults();
  }

  // Defaults in Payload CMS aktualisieren
  private async updatePayloadDefaults() {
    try {
      if (!this.defaultsId) {
        console.warn('Keine Defaults ID vorhanden, kann nicht in Payload CMS aktualisieren');
        return;
      }

      const body = {
        companyName: this.defaultCompanyInfo,
        deliveryNote: this.defaultDeliveryTime,
        agentNote: this.defaultAgentNote,
        karmaPhone: this.karmaPhone,
        karmaEmail: this.karmaEmail,
        karmaDeliveryDate: this.karmaDeliveryDate,
        karmaPaymentId: this.karmaPaymentId,
        karmaNote: this.karmaNote,
      };

      const response = await this.dataServer.updateDefaults(this.defaultsId, body);

      // silently succeeds
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Defaults in Payload CMS:', error);
    }
  }

  // Standard-Lieferzeit abrufen
  getDefaultDeliveryTime(): string {
    return this.defaultDeliveryTime;
  }

  // Standard-Firmeninfo abrufen
  getDefaultCompanyInfo(): string {
    return this.defaultCompanyInfo;
  }

  // Standard-Agenturnotiz abrufen
  getDefaultAgentNote(): string {
    return this.defaultAgentNote;
  }

  async setKarmaPhone(value: string) {
    this.karmaPhone = value;
    await this.storage.setData('karmaPhone', value);
    await this.updatePayloadDefaults();
  }

  async setKarmaEmail(value: string) {
    this.karmaEmail = value;
    await this.storage.setData('karmaEmail', value);
    await this.updatePayloadDefaults();
  }

  async setKarmaDeliveryDate(value: string) {
    this.karmaDeliveryDate = value;
    await this.storage.setData('karmaDeliveryDate', value);
    await this.updatePayloadDefaults();
  }

  async setKarmaPaymentId(value: string) {
    this.karmaPaymentId = value;
    await this.storage.setData('karmaPaymentId', value);
    await this.updatePayloadDefaults();
  }

  async setKarmaNote(value: string) {
    this.karmaNote = value;
    await this.storage.setData('karmaNote', value);
    await this.updatePayloadDefaults();
  }


  async checkToken() {

    const status = await this.checkTokenValid();
    this.tokenvalid = status;
    const time = await this.getTokenLifetime();
    this.tokenLifetime = time;

    return { status, time };
  }

  /** Lexware-Session-Renewal wird nicht mehr benötigt — gibt immer true zurück. */
  async greenlightSession(): Promise<boolean> {
    return true;
  }

  async getSessionStatus() {
    return "true"
  }

  private getTokenExpiryDateTime(): DateTime | null {
    const exp = this.token?.exp;
    if (typeof exp !== 'number') return null;
    return DateTime.fromSeconds(exp);
  }

  checkTokenValid(): boolean {
    const expiry = this.getTokenExpiryDateTime();
    if (!expiry) return false;
    return expiry.minus({ seconds: this.TOKEN_EXPIRY_BUFFER }) > DateTime.now();
  }

  getTokenLifetime(): number {
    const expiry = this.getTokenExpiryDateTime();
    if (!expiry) return 0;
    const diff = expiry.diff(DateTime.now(), 'seconds').seconds;
    return diff > 0 ? Math.floor(diff) : 0;
  }

  getTokenDaysRemaining(): number {
    const expiry = this.getTokenExpiryDateTime();
    if (!expiry) return 0;
    const diff = expiry.diff(DateTime.now(), 'days').days;
    return diff > 0 ? Math.floor(diff) : 0;
  }
}
